import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Car, Download, FileText, FileSpreadsheet } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

export default function InvestorsPage() {
  const [investors, setInvestors] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [iRes, vRes] = await Promise.all([api.get("/users?role=investisseur"), api.get("/vehicles")]);
      setInvestors(iRes.data);
      setVehicles(vRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, role: "investisseur" };
      if (!payload.pin) delete payload.pin;
      if (selectedInvestor) {
        await api.put(`/users/${selectedInvestor.id}`, payload);
        toast.success("Investisseur mis a jour");
      } else {
        await api.post("/users", payload);
        toast.success("Investisseur ajoute");
      }
      setShowAdd(false);
      setSelectedInvestor(null);
      setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet investisseur ?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Investisseur supprime");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (inv) => {
    setSelectedInvestor(inv);
    setForm({ nom: inv.nom, prenom: inv.prenom, email: inv.email || "", telephone: inv.telephone || "", pin: "" });
    setShowAdd(true);
  };

  const getVehiclesForInvestor = (investorId) => vehicles.filter(v => v.investisseur_id === investorId);

  const invExportCols = [
    { label: "Prenom", key: "prenom" },
    { label: "Nom", key: "nom" },
    { label: "Email", key: "email" },
    { label: "Telephone", key: "telephone" },
    { label: "PIN", accessor: (inv) => inv.has_pin ? "Configure" : "Non defini" },
    { label: "Vehicules", accessor: (inv) => getVehiclesForInvestor(inv.id).map(v => v.immatriculation).join(", ") || "Aucun" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="investors-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Investisseurs</h1>
          <p className="text-sm text-slate-500 mt-1">{investors.length} investisseur(s)</p>
        </div>
        <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setSelectedInvestor(null); setForm({ nom: "", prenom: "", email: "", telephone: "" }); setShowAdd(true); }} data-testid="btn-add-investor">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <div className="flex justify-end mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="btn-export-investors">
              <Download className="w-4 h-4 mr-2" />Exporter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportCSV(investors, invExportCols, "investisseurs")} data-testid="export-investors-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportExcel(investors, invExportCols, "investisseurs")} data-testid="export-investors-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportPDF(investors, invExportCols, "investisseurs", "Liste des investisseurs")} data-testid="export-investors-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Nom</TableHead>
              <TableHead className="font-semibold text-slate-600">Email</TableHead>
              <TableHead className="font-semibold text-slate-600">Telephone</TableHead>
              <TableHead className="font-semibold text-slate-600">Code PIN</TableHead>
              <TableHead className="font-semibold text-slate-600">Vehicules</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {investors.map(inv => {
              const invVehicles = getVehiclesForInvestor(inv.id);
              return (
                <TableRow key={inv.id} data-testid={`investor-row-${inv.id}`}>
                  <TableCell className="font-medium text-slate-900">{inv.prenom} {inv.nom}</TableCell>
                  <TableCell className="text-slate-600">{inv.email || "-"}</TableCell>
                  <TableCell className="text-slate-600">{inv.telephone || "-"}</TableCell>
                  <TableCell>
                    {inv.has_pin ? (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Configure</Badge>
                    ) : (
                      <Badge variant="outline" className="bg-slate-50 text-slate-400 border-slate-200">Non defini</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {invVehicles.length > 0 ? invVehicles.map(v => (
                        <Badge key={v.id} variant="outline" className="text-xs">
                          <Car className="w-3 h-3 mr-1" />{v.immatriculation}
                        </Badge>
                      )) : <span className="text-slate-400 text-sm">Aucun vehicule</span>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(inv)} data-testid={`btn-edit-investor-${inv.id}`}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(inv.id)} data-testid={`btn-delete-investor-${inv.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {investors.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  Aucun investisseur. Cliquez sur "Ajouter" pour en creer un.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Summary Cards */}
      {investors.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
          {investors.map(inv => {
            const invVehicles = getVehiclesForInvestor(inv.id);
            const actifs = invVehicles.filter(v => v.statut === "actif").length;
            const immobilises = invVehicles.filter(v => v.statut === "immobilise").length;
            return (
              <Card key={inv.id} className="hover:shadow-md transition-shadow" data-testid={`investor-card-${inv.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#0F52BA]" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-heading">{inv.prenom} {inv.nom}</CardTitle>
                      <p className="text-xs text-slate-500">{inv.email || inv.telephone || ""}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4 text-sm">
                    <div>
                      <p className="text-lg font-bold text-slate-900 font-heading">{invVehicles.length}</p>
                      <p className="text-xs text-slate-500">Vehicule(s)</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600 font-heading">{actifs}</p>
                      <p className="text-xs text-slate-500">Actif(s)</p>
                    </div>
                    {immobilises > 0 && (
                      <div>
                        <p className="text-lg font-bold text-red-600 font-heading">{immobilises}</p>
                        <p className="text-xs text-slate-500">Immobilise(s)</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="investor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selectedInvestor ? "Modifier l'investisseur" : "Ajouter un investisseur"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prenom</Label>
                <Input value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} required data-testid="input-investor-prenom" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} required data-testid="input-investor-nom" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="input-investor-email" />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} data-testid="input-investor-telephone" />
              </div>
            </div>
            <div>
              <Label>Code PIN <span className="text-xs text-slate-400 font-normal">(4 ou 6 chiffres, optionnel{selectedInvestor ? " - laisser vide pour ne pas modifier" : ""})</span></Label>
              <Input
                type="password"
                maxLength={6}
                placeholder="4 ou 6 chiffres"
                value={form.pin}
                onChange={e => setForm({...form, pin: e.target.value.replace(/\D/g, "")})}
                data-testid="input-investor-pin"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-investor">
                {selectedInvestor ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
